/**
 * Group page (#/group) device grid HTML + virtual mount (FE-G1).
 */

/**
 * @param {object} d
 * @param {object} keys
 * @param {(s: string) => string} keys.escapeHtml
 * @param {(d: object) => { cls: string }} keys.devicePresenceStatus
 * @param {(pres: object) => string} keys.devicePresenceBadgeText
 * @param {(d: object) => string} keys.crocFmtHwModelPrimary
 * @param {string} keys.BRAND_HARDWARE_MANUFACTURER
 * @returns {string}
 */
export function buildGroupPageDeviceCardHtml(d, keys) {
  const pres = keys.devicePresenceStatus(d);
  const badgeTxt = keys.devicePresenceBadgeText(pres);
  const primary = keys.escapeHtml(d.display_label || d.device_id || "unknown");
  const subId = d.display_label
    ? `<div class="device-id-sub mono">${keys.escapeHtml(d.device_id || "")}</div>`
    : "";
  return `<a class="device-card" href="#/devices/${encodeURIComponent(d.device_id)}" style="text-decoration:none;color:inherit">
        <h3><div class="device-primary-name">${primary}</div>${subId}</h3>
        <div><span class="badge ${pres.cls}">${badgeTxt}</span>
          ${d.zone ? `<span class="chip">${keys.escapeHtml(d.zone)}</span>` : ""}
          ${d.fw ? `<span class="chip">v${keys.escapeHtml(d.fw)}</span>` : ""}
          ${d.is_shared ? `<span class="badge accent" title="shared board">sharing by ${keys.escapeHtml(d.shared_by || "?")}</span>` : ""}
        </div>
        <div class="meta">Model: ${keys.escapeHtml(keys.crocFmtHwModelPrimary(d))}<br/>Manufacturer: ${keys.escapeHtml(keys.BRAND_HARDWARE_MANUFACTURER)}</div>
      </a>`;
}

/**
 * @param {object} ctx
 * @param {HTMLElement|null} ctx.devGrid
 * @param {HTMLElement} ctx.view
 * @param {() => object[]} ctx.getRows
 * @param {(d: object) => boolean} ctx.deviceReachable
 * @param {(container: HTMLElement, rows: string[], opts?: object) => () => void} ctx.mountDeviceGridVirtualList
 * @param {number} ctx.virtualMin
 * @param {number} ctx.virtualRowPx
 * @param {(el: HTMLElement|null, html: string) => void} ctx.setChildMarkup
 * @param {(sel: string, root?: HTMLElement) => HTMLElement|null} ctx.$
 * @param {(d: object) => string} ctx.buildCardHtml
 * @returns {{ render: () => void, disposeVirtual: () => void }}
 */
export function createGroupPageDeviceGrid(ctx) {
  let virtualDispose = null;

  const disposeVirtual = () => {
    if (typeof virtualDispose === "function") {
      try {
        virtualDispose();
      } catch (_) {}
      virtualDispose = null;
    }
  };

  const render = () => {
    const devGrid = ctx.devGrid;
    if (!devGrid) return;
    disposeVirtual();
    const rows = ctx.getRows();
    if (!rows.length) {
      ctx.setChildMarkup(devGrid, `<p class="muted">No boards in this group.</p>`);
    } else {
      const htmlRows = rows.map((d) => ctx.buildCardHtml(d));
      if (htmlRows.length < ctx.virtualMin) {
        ctx.setChildMarkup(devGrid, htmlRows.join(""));
      } else {
        devGrid.innerHTML = "";
        virtualDispose = ctx.mountDeviceGridVirtualList(devGrid, htmlRows, {
          rowPx: ctx.virtualRowPx,
        });
      }
    }
  };

  return { render, disposeVirtual };
}
